import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, User, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from "./ui/button";
import { useConsultationModal } from '../context/ConsultationModalContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tenzing Avatar Image URL
const TENZING_AVATAR_URL = "https://customer-assets.emergentagent.com/job_teams-notify/artifacts/l8fnbj0j_image.png";

// Generate a unique session ID
const generateSessionId = () => {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Tenzing Avatar Component
const TenzingAvatar = ({ size = 'md' }) => {
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-14 h-14'
    };
    
    return (
        <div className={`${sizeClasses[size]} relative flex-shrink-0 rounded-full overflow-hidden border-2 border-accent/50 shadow-lg bg-slate-900`}>
            <img 
                src={TENZING_AVATAR_URL} 
                alt="Tenzing - EverestCloud AI Assistant"
                className="w-full h-full object-cover scale-110 object-[center_20%]"
            />
        </div>
    );
};

// Validation helpers
const validateName = (name) => {
    // Only allow letters, spaces, and common name characters (no numbers or special symbols)
    const nameRegex = /^[a-zA-Z\s\-'\.]+$/;
    return name.trim().length >= 2 && name.trim().length <= 50 && nameRegex.test(name.trim());
};

const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email.trim().length <= 100 && emailRegex.test(email.trim());
};

const validatePhone = (phone) => {
    if (!phone.trim()) return true; // Optional field
    // Allow digits, +, -, spaces, and parentheses
    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    return phone.trim().length >= 7 && phone.trim().length <= 20 && phoneRegex.test(phone.trim());
};

const validateCompany = (company) => {
    if (!company.trim()) return true; // Optional field
    // Allow letters, numbers, spaces, and common business characters
    const companyRegex = /^[a-zA-Z0-9\s\-\.\,\&\(\)\']+$/;
    return company.trim().length >= 2 && company.trim().length <= 100 && companyRegex.test(company.trim());
};

// Sanitize input to prevent XSS
const sanitizeInput = (input) => {
    if (!input) return '';
    return input
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .trim();
};

// Lead Capture Form Component
const LeadCaptureForm = ({ onSubmit, onSkip, isSubmitting }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        industry: ''
    });
    const [step, setStep] = useState(0);
    const [error, setError] = useState('');
    
    const industryOptions = [
        { value: 'banking', label: 'Banking & Financial Services' },
        { value: 'government', label: 'Government & Public Sector' },
        { value: 'telecom', label: 'Telecom & ISP' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'retail', label: 'Retail & E-commerce' },
        { value: 'education', label: 'Education' },
        { value: 'other', label: 'Other' }
    ];
    
    const steps = [
        { field: 'name', label: "What's your name?", placeholder: "Rajesh Sharma", type: 'text', required: true, validate: validateName, errorMsg: "Please enter a valid name (2-50 letters)", maxLength: 50 },
        { field: 'email', label: "Great! What's your email address?", placeholder: "rajesh@company.com.np", type: 'email', required: true, validate: validateEmail, errorMsg: "Please enter a valid email address", maxLength: 100 },
        { field: 'phone', label: "Your phone number?", placeholder: "+977-98XXXXXXXX", type: 'tel', required: true, validate: validatePhone, errorMsg: "Please enter a valid phone number (7-20 digits)", maxLength: 20 },
        { field: 'company', label: "Which company are you from?", placeholder: "Nepal Commercial Bank Limited", type: 'text', required: false, validate: validateCompany, errorMsg: "Please enter a valid company name", maxLength: 100 },
        { field: 'industry', label: "What industry are you in?", type: 'select', options: industryOptions, required: false }
    ];
    
    const currentStep = steps[step];
    
    const handleNext = () => {
        const value = formData[currentStep.field];
        
        // Validate current field
        if (currentStep.required && !value.trim()) {
            setError("This field is required");
            return;
        }
        
        if (value.trim() && currentStep.validate && !currentStep.validate(value)) {
            setError(currentStep.errorMsg);
            return;
        }
        
        setError('');
        
        if (step < steps.length - 1) {
            setStep(step + 1);
        } else {
            onSubmit(formData);
        }
    };
    
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleNext();
        }
    };

    const handleChange = (e) => {
        setError('');
        setFormData({ ...formData, [currentStep.field]: e.target.value });
    };
    
    return (
        <div className="space-y-3 p-1">
            <p className="text-sm text-slate-700 dark:text-slate-200">{currentStep.label}</p>
            <div className="flex gap-2">
                {currentStep.type === 'select' ? (
                    <select
                        value={formData[currentStep.field]}
                        onChange={handleChange}
                        className={`flex-1 px-3 py-2 bg-white dark:bg-slate-800 border ${error ? 'border-red-400' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50`}
                        disabled={isSubmitting}
                    >
                        <option value="">Select an option</option>
                        {currentStep.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type={currentStep.type}
                        value={formData[currentStep.field]}
                        onChange={handleChange}
                        onKeyPress={handleKeyPress}
                        placeholder={currentStep.placeholder}
                        maxLength={currentStep.maxLength}
                        className={`flex-1 px-3 py-2 bg-white dark:bg-slate-800 border ${error ? 'border-red-400' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50`}
                        autoFocus
                        disabled={isSubmitting}
                    />
                )}
                <Button
                    onClick={handleNext}
                    disabled={isSubmitting}
                    size="sm"
                    className="px-3 bg-accent hover:bg-accent/90 text-black"
                >
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                </Button>
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
            <div className="flex items-center justify-between">
                <div className="flex gap-1">
                    {steps.map((_, idx) => (
                        <div 
                            key={idx} 
                            className={`w-2 h-2 rounded-full ${idx <= step ? 'bg-accent' : 'bg-slate-300 dark:bg-slate-600'}`}
                        />
                    ))}
                </div>
                {!currentStep.required && (
                    <button 
                        onClick={handleNext}
                        className="text-xs text-slate-500 hover:text-accent"
                        disabled={isSubmitting}
                    >
                        Skip this
                    </button>
                )}
            </div>
        </div>
    );
};

const ChatBot = () => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: "Namaste! I'm Tenzing, your EverestCloud guide. Like my namesake who conquered Everest, I'm here to help you reach new heights with Nepal's sovereign cloud. How can I assist you today?"
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId] = useState(() => generateSessionId());
    const [showLeadForm, setShowLeadForm] = useState(false);
    const [leadSubmitted, setLeadSubmitted] = useState(false);
    const [leadContext, setLeadContext] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const { openModal } = useConsultationModal();

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, showLeadForm]);

    useEffect(() => {
        if (isOpen && !showLeadForm) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen, showLeadForm]);

    // Hide ChatBot on presentation mode (placed after all hooks)
    if (location.pathname === '/present') {
        return null;
    }

    const handleLeadSubmit = async (leadData) => {
        setIsLoading(true);
        try {
            await axios.post(`${API}/chat/lead`, {
                session_id: sessionId,
                name: leadData.name,
                email: leadData.email,
                phone: leadData.phone,
                company: leadData.company,
                industry: leadData.industry,
                context: leadContext,
                chat_history: messages.slice(1).map(m => `${m.role}: ${m.content}`).join('\n')
            });
            
            setLeadSubmitted(true);
            setShowLeadForm(false);
            
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `Thank you ${leadData.name}! I've shared your details with our team. They'll reach out to you at ${leadData.email} shortly. Is there anything else I can help you with in the meantime?`
            }]);
        } catch (error) {
            console.error('Lead submission error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "I'm sorry, there was an issue saving your details. Please try again or contact us directly at info@everestcloud.com.np"
            }]);
        } finally {
            setIsLoading(false);
            setShowLeadForm(false);
        }
    };

    const handleSend = async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage = inputValue.trim();
        setInputValue('');
        
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsLoading(true);

        try {
            const response = await axios.post(`${API}/chat`, {
                session_id: sessionId,
                message: userMessage
            });

            const { response: aiResponse, suggest_handoff, handoff_type, request_contact } = response.data;
            
            if (request_contact && !leadSubmitted) {
                setLeadContext(userMessage);
                setMessages(prev => [...prev, { 
                    role: 'assistant', 
                    content: aiResponse,
                    showLeadCapture: true
                }]);
                setShowLeadForm(true);
            } else {
                setMessages(prev => [...prev, { 
                    role: 'assistant', 
                    content: aiResponse,
                    suggestHandoff: suggest_handoff,
                    handoffType: handoff_type
                }]);
            }
        } catch (error) {
            console.error('Chat error:', error);
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us at info@everestcloud.com.np.",
                suggestHandoff: true,
                handoffType: 'general'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleHandoff = (type) => {
        setIsOpen(false);
        openModal(type || 'general');
    };

    const triggerLeadCapture = () => {
        setLeadContext('User requested to share contact details');
        setShowLeadForm(true);
        setMessages(prev => [...prev, {
            role: 'assistant',
            content: "I'd love to connect you with our team! Let me get a few details from you."
        }]);
    };

    const quickActions = [
        { label: "What services do you offer?", query: "What services does EverestCloud offer?" },
        { label: "Tell me about pricing", query: "Can you tell me about pricing?" },
        { label: "Data sovereignty", query: "How do you ensure data sovereignty?" },
    ];

    return (
        <>
            {/* Chat Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-6 right-6 z-50 group transition-all duration-300 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
                aria-label="Open chat"
                data-testid="chat-toggle-button"
            >
                <div className="relative">
                    <div className="absolute inset-0 bg-accent rounded-full blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
                    <div className="relative w-16 h-16 bg-gradient-to-br from-slate-800 to-slate-900 rounded-full flex items-center justify-center shadow-2xl border-2 border-accent/50 group-hover:border-accent transition-all group-hover:scale-110">
                        <TenzingAvatar size="lg" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                </div>
            </button>

            {/* Chat Window */}
            <div 
                className={`fixed bottom-6 right-6 z-50 w-[400px] h-[580px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-white/10 flex flex-col overflow-hidden transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}`}
                data-testid="chat-window"
            >
                {/* Header - Matches site theme */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 flex items-center gap-3 border-b border-accent/20">
                    <TenzingAvatar size="md" />
                    <div className="flex-1">
                        <h3 className="text-white font-bold text-lg">Tenzing</h3>
                        <p className="text-accent text-xs">Your EverestCloud Guide</p>
                    </div>
                    <button 
                        onClick={() => setIsOpen(false)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        aria-label="Close chat"
                        data-testid="chat-close-button"
                    >
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 dark:bg-slate-950">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                            {msg.role === 'assistant' ? (
                                <TenzingAvatar size="sm" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                                    <User className="w-4 h-4 text-black" />
                                </div>
                            )}
                            <div className={`max-w-[80%] ${msg.role === 'user' ? 'bg-accent text-black' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200'} rounded-2xl px-4 py-3 shadow-sm ${msg.role === 'user' ? 'rounded-br-md' : 'rounded-bl-md'}`}>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                                
                                {msg.suggestHandoff && msg.handoffType && !showLeadForm && (
                                    <button
                                        onClick={() => handleHandoff(msg.handoffType)}
                                        className="mt-3 flex items-center gap-2 text-xs font-medium text-accent hover:text-accent/80 transition-colors"
                                        data-testid="handoff-button"
                                    >
                                        <ArrowRight className="w-3 h-3" />
                                        Talk to our team
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                    
                    {/* Lead Capture Form */}
                    {showLeadForm && !leadSubmitted && (
                        <div className="flex gap-3">
                            <TenzingAvatar size="sm" />
                            <div className="max-w-[85%] bg-white dark:bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-accent/30">
                                <LeadCaptureForm 
                                    onSubmit={handleLeadSubmit}
                                    onSkip={() => setShowLeadForm(false)}
                                    isSubmitting={isLoading}
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Loading indicator */}
                    {isLoading && !showLeadForm && (
                        <div className="flex gap-3">
                            <TenzingAvatar size="sm" />
                            <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                                <div className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin text-accent" />
                                    <span className="text-sm text-slate-500">Tenzing is thinking...</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                </div>

                {/* Quick Actions */}
                {messages.length === 1 && !showLeadForm && (
                    <div className="px-4 py-2 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-white/5">
                        <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
                        <div className="flex flex-wrap gap-2">
                            {quickActions.map((action, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        setInputValue(action.query);
                                        setTimeout(() => handleSend(), 100);
                                    }}
                                    className="text-xs px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full hover:bg-accent/20 hover:text-accent dark:hover:bg-accent/20 dark:hover:text-accent transition-colors"
                                    data-testid={`quick-action-${idx}`}
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Input */}
                <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-white/5">
                    {!showLeadForm && (
                        <>
                            <div className="flex gap-2">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Ask Tenzing anything..."
                                    className="flex-1 px-4 py-3 bg-slate-100 dark:bg-slate-800 border-0 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50"
                                    disabled={isLoading}
                                    data-testid="chat-input"
                                />
                                <Button
                                    onClick={handleSend}
                                    disabled={!inputValue.trim() || isLoading}
                                    className="px-4 bg-accent hover:bg-accent/90 text-black rounded-xl"
                                    data-testid="chat-send-button"
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            </div>
                            
                            {/* Lead capture & human handoff links */}
                            <div className="mt-2 flex items-center justify-center gap-3 text-xs">
                                {!leadSubmitted && (
                                    <button
                                        onClick={triggerLeadCapture}
                                        className="text-accent hover:text-accent/80 transition-colors flex items-center gap-1"
                                        data-testid="share-contact-button"
                                    >
                                        <CheckCircle2 className="w-3 h-3" />
                                        Share my contact
                                    </button>
                                )}
                                <span className="text-slate-300 dark:text-slate-600">|</span>
                                <button
                                    onClick={() => handleHandoff('general')}
                                    className="text-slate-500 hover:text-accent transition-colors"
                                    data-testid="talk-to-human-button"
                                >
                                    Talk to a human
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ChatBot;
